import React, { useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { RootStore } from 'stores';
import _ from 'lodash';
import {
    DataTablePresenter,
    isTabletPortraitDown,
    ITableColumn,
    ProviderCodingRule,
    Spinner,
    Swatches,
    Tooltip,
    Variables,
} from '@enter/core';
import * as S from './shared.styles';
import { Icon } from '@mdi/react';
import { mdiArrowLeft, mdiCheck, mdiClose } from '@mdi/js';
import codingRulesStore from 'stores/coding-rules.store';
import styled from '@emotion/styled/macro';

const StatusCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const DescriptionCell = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: help;
`;

const TooltipContent = styled.div`
    padding: 8px 12px;
    max-width: 400px;
    word-wrap: break-word;
    white-space: normal;
`;

const ErrorMessage = styled.div`
    padding: 40px;
    text-align: center;
    color: ${Variables.Colors.warning};
    font-size: 16px;
`;

export const CodingRules: React.FunctionComponent<{}> = inject('rootStore')(
    observer(
        (props: {}): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            const [error, setError] = useState<string | null>(null);

            if (!rootStore) {
                return <></>;
            }

            useEffect(() => {
                (async () => {
                    try {
                        await codingRulesStore.fetch();
                        setError(null);
                    } catch (err) {
                        console.error('Failed to fetch coding rules:', err);
                        setError('Failed to load coding rules. Please try again later.');
                    }
                })();
            }, []);

            const columns: ITableColumn<ProviderCodingRule>[] = useMemo(
                () => [
                    {
                        id: 'RuleNumber',
                        Header: () => <S.TableHeader>Rule Number</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => (
                            <S.TableCell>{row.original.ruleNumber}</S.TableCell>
                        ),
                        width: 100,
                    },
                    {
                        id: 'Description',
                        Header: () => <S.TableHeader>Description</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => (
                            <Tooltip
                                direction="top"
                                title={<TooltipContent>{row.original.description}</TooltipContent>}>
                                <DescriptionCell>{row.original.description}</DescriptionCell>
                            </Tooltip>
                        ),
                    },
                    {
                        id: 'Status',
                        Header: () => <S.TableHeader>Status</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => (
                            <StatusCell>
                                {row.original.isActive ? (
                                    <Icon path={mdiCheck} size={0.8} color={Swatches.Green6} />
                                ) : (
                                    <Icon path={mdiClose} size={0.8} color={Swatches.Red6} />
                                )}
                            </StatusCell>
                        ),
                        width: 80,
                    },
                    {
                        id: 'Created',
                        Header: () => <S.TableHeader>Created</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => {
                            const date = new Date(row.original.created);
                            return <S.TableCell>{date.toLocaleDateString()}</S.TableCell>;
                        },
                        width: 120,
                    },
                ],
                [],
            );

            const buildDataPresenter = (): DataTablePresenter<ProviderCodingRule> => {
                return new DataTablePresenter({
                    name: 'codingRules',
                    data: codingRulesStore.rules,
                    columns,
                    filterSchemas: [],
                });
            };

            if (codingRulesStore.loading && codingRulesStore.rules.length === 0) {
                return <Spinner />;
            }

            if (error) {
                return (
                    <S.SubCategoryCol>
                        <S.SubCategoryItem key="coding-rules">
                            <S.SubCategoryItemRow>
                                <S.SubCategoryH1>
                                    {isTabletPortraitDown() && (
                                        <S.ReturnLink to="/settings">
                                            {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                        </S.ReturnLink>
                                    )}
                                    Coding Rules
                                </S.SubCategoryH1>
                            </S.SubCategoryItemRow>
                            <ErrorMessage>{error}</ErrorMessage>
                        </S.SubCategoryItem>
                    </S.SubCategoryCol>
                );
            }

            return (
                <S.SubCategoryCol>
                    <S.SubCategoryItem key="coding-rules">
                        <S.SubCategoryItemRow>
                            <S.SubCategoryH1>
                                {isTabletPortraitDown() && (
                                    <S.ReturnLink to="/settings">
                                        {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                    </S.ReturnLink>
                                )}
                                Coding Rules
                            </S.SubCategoryH1>
                        </S.SubCategoryItemRow>
                        <S.Table
                            presenter={buildDataPresenter()}
                            hideFiltering={true}
                            hidePagination={true}
                            hideSettings={true}
                        />
                    </S.SubCategoryItem>
                </S.SubCategoryCol>
            );
        },
    ),
);
